// ============================================================================
// ReminderModal — compose an onboarding reminder for one or many records.
// Channel picker (Email / Google Chat — Chat is a future placeholder). Body
// is pre-populated from each record's missing-items summary. There is no
// email/Chat integration wired yet, so the action is Copy-to-clipboard + a
// toast; the compose surface is the deliverable per spec (§3/§4 "Send
// Reminder" → composes a reminder pre-populated with the missing-items
// summary). Flagged in the session report as needing a real send transport.
// ============================================================================
import { useMemo, useState } from 'react';
import { X, Copy, Send } from 'lucide-react';
import { Button, useToast } from '../../shared/components';
import { effectiveChecklist, missingItemsSummary } from './utils';
import type { OnboardingRecord } from './types';
import { overlayClickClose } from '../../shared/overlay-close';

type Channel = 'email' | 'chat';

export default function ReminderModal({
  records,
  onClose,
}: {
  records: OnboardingRecord[];
  onClose: () => void;
}) {
  const toast = useToast();
  const [channel, setChannel] = useState<Channel>('email');

  // A reminder about subs only says "setup"; one that includes a W2 hire keeps "onboarding" (item 871 follow-on).
  const reminderWord = records.length > 0 && records.every((r) => r.type !== 'W2') ? 'Setup' : 'Onboarding';
  const defaultSubject =
    records.length === 1
      ? `${reminderWord} reminder — ${records[0].name}`
      : `${reminderWord} reminder — ${records.length} records`;
  const [subject, setSubject] = useState(defaultSubject);

  const defaultBody = useMemo(() => {
    return records
      .map((r) => {
        const missing = missingItemsSummary(
          effectiveChecklist(r.checklist),
          r.employmentTypeForChecklist,
        );
        const lines = missing.length
          ? missing.map((m) => `  • ${m}`).join('\n')
          : '  • (nothing outstanding)';
        return `${r.name}${r.account ? ` (${r.account})` : ''} — outstanding items:\n${lines}`;
      })
      .join('\n\n');
  }, [records]);
  const [body, setBody] = useState(defaultBody);

  async function copy() {
    const text = `Subject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Reminder copied to clipboard', {
        description: 'Paste into email / Google Chat to send.',
      });
    } catch {
      toast.info('Copy unavailable', { description: 'Select the text manually to copy.' });
    }
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Compose reminder"
      {...overlayClickClose(onClose)}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-tcs-overlay shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-tcs-border px-4 py-3">
          <div className="text-sm font-semibold text-tcs-text">
            Send reminder · {records.length} recipient{records.length === 1 ? '' : 's'}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-tcs-surface">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 overflow-y-auto p-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-tcs-text-secondary">Channel</label>
            <div className="flex gap-2">
              <ChannelPill label="Email" active={channel === 'email'} onClick={() => setChannel('email')} />
              <ChannelPill
                label="Google Chat (soon)"
                active={channel === 'chat'}
                onClick={() => setChannel('chat')}
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-tcs-text-secondary">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-tcs-text-secondary">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="w-full resize-y rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 font-mono text-xs"
            />
          </div>
          {channel === 'chat' && (
            <p className="text-xs text-st-warn-fg">
              Google Chat delivery isn't wired yet — copy the message and paste it for now.
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-tcs-border px-4 py-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="secondary" icon={<Copy className="h-3.5 w-3.5" />} onClick={copy}>
            Copy
          </Button>
          <Button
            size="sm"
            icon={<Send className="h-3.5 w-3.5" />}
            onClick={() => {
              copy();
              onClose();
            }}
          >
            Compose &amp; close
          </Button>
        </footer>
      </div>
    </div>
  );
}

function ChannelPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
        active
          ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
          : 'border-tcs-border bg-tcs-bg text-tcs-text-secondary hover:bg-tcs-surface'
      }`}
    >
      {label}
    </button>
  );
}
